import React, { useState, useEffect } from 'react';
import { UseFormRegister, UseFormWatch, UseFormSetValue, FieldErrors } from 'react-hook-form';
import { Icon } from '@/shared/icons'
import type { IconName } from '@/shared/icons'
interface SocialMediaFormProps {
  register: UseFormRegister<any>;
  watch: UseFormWatch<any>;
  setValue: UseFormSetValue<any>;
  errors: FieldErrors<any>;
}

const ATTACHMENT_TYPES = [
  { value: 'image', label: 'Imagem', icon: 'image', description: 'JPG, PNG, GIF' },
  { value: 'video', label: 'Vídeo', icon: 'video', description: 'MP4, MOV' },
  { value: 'audio', label: 'Áudio', icon: 'music', description: 'MP3, WAV' },
  { value: 'file', label: 'Arquivo', icon: 'file', description: 'PDF, DOC, etc.' },
];

interface QuickReply {
  content_type: 'text';
  title: string;
  payload?: string;
}

export const SocialMediaForm: React.FC<SocialMediaFormProps> = ({ register, watch, setValue, errors }) => {
  const hasAttachment = watch('providerConfig.messageData.attachment') !== undefined;
  const attachmentType = watch('providerConfig.messageData.attachment.type');
  const [quickReplies, setQuickReplies] = useState<QuickReply[]>([]);

  // ✅ CRITICAL: Inicializar valores padrão obrigatórios
  const providerId = watch('providerId');
  useEffect(() => {
    if (providerId === 'instagram_direct') {
      setValue('providerConfig.providerType', 'instagram_direct');
    } else if (providerId === 'facebook_messenger') {
      setValue('providerConfig.providerType', 'facebook_messenger');
    }

    // Inicializar messageData se não estiver definido
    if (!watch('providerConfig.messageData')) {
      setValue('providerConfig.messageData', { text: '' });
    }
  }, [providerId, setValue]);

  const toggleAttachment = () => {
    if (hasAttachment) {
      setValue('providerConfig.messageData.attachment', undefined);
    } else {
      setValue('providerConfig.messageData.attachment', { type: 'image', url: '' });
    }
  };

  const setAttachmentType = (type: string) => {
    const currentUrl = watch('providerConfig.messageData.attachment.url') || '';
    setValue('providerConfig.messageData.attachment', { type, url: currentUrl });
  };

  const addQuickReply = () => {
    const newReplies = [...quickReplies, { content_type: 'text' as const, title: '', payload: '' }];
    setQuickReplies(newReplies);
    setValue('providerConfig.messageData.quick_replies', newReplies);
  };

  const removeQuickReply = (index: number) => {
    const newReplies = quickReplies.filter((_, i) => i !== index);
    setQuickReplies(newReplies);
    setValue('providerConfig.messageData.quick_replies', newReplies);
  };

  const updateQuickReply = (index: number, field: 'title' | 'payload', value: string) => {
    const newReplies = [...quickReplies];
    newReplies[index] = { ...newReplies[index], [field]: value };
    setQuickReplies(newReplies);
    setValue('providerConfig.messageData.quick_replies', newReplies);
  };

  return (
    <div className="space-y-6">
      {/* Text Message */}
      <div>
        <label className="block text-sm font-medium mb-2" style={{ color: 'var(--theme-card-text)' }}>
          Mensagem <span style={{ color: 'var(--theme-error-color)' }}>*</span>
        </label>
        <textarea
          {...register('providerConfig.messageData.text')}
          placeholder="Digite a mensagem. Use {{1}}, {{2}}, etc. para variáveis"
          rows={6}
          className="w-full px-4 py-3 rounded-lg border text-sm resize-none"
          style={{
            backgroundColor: 'var(--theme-input-background)',
            borderColor: 'var(--theme-input-border)',
            color: 'var(--theme-input-text)',
          }}
        />
        <div className="flex items-center gap-2 mt-2">
          <Icon name="alert-circle" className="w-4 h-4" style={{ color: 'var(--theme-info-color)' }} />
          <p className="text-xs" style={{ color: 'var(--theme-muted-text)' }}>
            Use variáveis numéricas: {'{'}
            {'{'}1{'}}'}
            {'}'}, {'{'}
            {'{'}2{'}}'}
            {'}'}, {'{'}
            {'{'}3{'}}'}
            {'}'} para personalização
          </p>
        </div>
        {(errors.providerConfig as any)?.messageData?.text && (
          <p className="text-sm mt-1" style={{ color: 'var(--theme-error-color)' }}>
            {((errors.providerConfig as any).messageData as any).text.message as string}
          </p>
        )}
      </div>

      {/* Attachment Section */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <label className="block text-sm font-medium" style={{ color: 'var(--theme-card-text)' }}>
            Anexo de Mídia (opcional)
          </label>
          <button
            type="button"
            onClick={toggleAttachment}
            className="px-3 py-1.5 text-sm rounded-lg border transition-colors"
            style={{
              backgroundColor: hasAttachment ? 'var(--theme-error-background)' : 'var(--theme-primary-background)',
              borderColor: hasAttachment ? 'var(--theme-error-color)' : 'var(--theme-primary)',
              color: hasAttachment ? 'var(--theme-error-color)' : 'var(--theme-primary)',
            }}
          >
            {hasAttachment ? 'Remover Anexo' : 'Adicionar Anexo'}
          </button>
        </div>

        {hasAttachment && (
          <div className="space-y-4 p-4 rounded-lg border" style={{ borderColor: 'var(--theme-card-border)', backgroundColor: 'var(--theme-input-background)' }}>
            {/* Attachment Type Selection */}
            <div>
              <label className="block text-xs font-medium mb-2" style={{ color: 'var(--theme-muted-text)' }}>
                Tipo de Anexo
              </label>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {ATTACHMENT_TYPES.map(({ value, label, icon, description }) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => setAttachmentType(value)}
                    className="p-3 rounded-lg border transition-all text-center"
                    style={{
                      borderColor: attachmentType === value ? 'var(--theme-primary)' : 'var(--theme-input-border)',
                      backgroundColor: attachmentType === value ? 'var(--theme-primary-background)' : 'transparent',
                    }}
                  >
                    <Icon
                      name={icon as IconName}
                      className="w-5 h-5 mx-auto mb-1"
                      style={{ color: attachmentType === value ? 'var(--theme-primary)' : 'var(--theme-muted-text)' }}
                    />
                    <p className="text-xs font-medium" style={{ color: 'var(--theme-card-text)' }}>
                      {label}
                    </p>
                    <p className="text-xs mt-0.5" style={{ color: 'var(--theme-muted-text)' }}>
                      {description}
                    </p>
                  </button>
                ))}
              </div>
            </div>

            {/* Attachment URL */}
            <div>
              <label className="block text-xs font-medium mb-2" style={{ color: 'var(--theme-muted-text)' }}>
                URL do Anexo
              </label>
              <input
                type="text"
                {...register('providerConfig.messageData.attachment.url')}
                placeholder="https://exemplo.com/arquivo.jpg"
                className="w-full px-3 py-2 rounded-lg border text-sm h-[38px]"
                style={{
                  backgroundColor: 'var(--theme-input-background)',
                  borderColor: 'var(--theme-input-border)',
                  color: 'var(--theme-input-text)',
                }}
              />
              <p className="text-xs mt-1.5" style={{ color: 'var(--theme-muted-text)' }}>
                URL pública do arquivo. Use variáveis como {'{'}
                {'{'}1{'}}'}
                {'}'} para URLs dinâmicas
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Quick Replies */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <label className="block text-sm font-medium" style={{ color: 'var(--theme-card-text)' }}>
            Respostas Rápidas (opcional)
          </label>
          <button
            type="button"
            onClick={addQuickReply}
            disabled={quickReplies.length >= 11}
            className="px-3 py-1.5 text-sm rounded-lg border transition-colors flex items-center gap-2 disabled:opacity-50"
            style={{
              backgroundColor: 'var(--theme-input-background)',
              borderColor: 'var(--theme-input-border)',
              color: 'var(--theme-card-text)',
            }}
          >
            <Icon name="plus" className="w-4 h-4" />
            Adicionar Resposta
          </button>
        </div>

        {quickReplies.length > 0 && (
          <div className="space-y-3">
            {quickReplies.map((reply, index) => (
              <div
                key={index}
                className="flex gap-2 p-3 rounded-lg border"
                style={{
                  borderColor: 'var(--theme-card-border)',
                  backgroundColor: 'var(--theme-input-background)',
                }}
              >
                <div className="flex-1 space-y-2">
                  <input
                    type="text"
                    value={reply.title}
                    onChange={(e) => updateQuickReply(index, 'title', e.target.value)}
                    placeholder="Texto da resposta (max 20 caracteres)"
                    maxLength={20}
                    className="w-full px-3 py-2 rounded-lg border text-sm h-[38px]"
                    style={{
                      backgroundColor: 'var(--theme-input-background)',
                      borderColor: 'var(--theme-input-border)',
                      color: 'var(--theme-input-text)',
                    }}
                  />
                  <input
                    type="text"
                    value={reply.payload || ''}
                    onChange={(e) => updateQuickReply(index, 'payload', e.target.value)}
                    placeholder="Payload (opcional) - identificador interno"
                    className="w-full px-3 py-2 rounded-lg border text-sm h-[38px]"
                    style={{
                      backgroundColor: 'var(--theme-input-background)',
                      borderColor: 'var(--theme-input-border)',
                      color: 'var(--theme-input-text)',
                    }}
                  />
                </div>
                <button
                  type="button"
                  onClick={() => removeQuickReply(index)}
                  className="p-2 rounded transition-colors self-start"
                  style={{ color: 'var(--theme-error-color)' }}
                >
                  <Icon name="trash" className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        )}

        {quickReplies.length === 0 && (
          <div
            className="text-center py-8 rounded-lg border-2 border-dashed"
            style={{ borderColor: 'var(--theme-card-border)' }}
          >
            <Icon name="message-circle" className="w-8 h-8 mx-auto mb-2" style={{ color: 'var(--theme-muted-text)' }} />
            <p className="text-sm" style={{ color: 'var(--theme-muted-text)' }}>
              Nenhuma resposta rápida adicionada
            </p>
            <p className="text-xs mt-1" style={{ color: 'var(--theme-muted-text)' }}>
              Clique em "Adicionar Resposta" para começar
            </p>
          </div>
        )}

        <p className="text-xs mt-2" style={{ color: 'var(--theme-muted-text)' }}>
          Máximo de 11 respostas rápidas. Cada título pode ter até 20 caracteres.
        </p>
      </div>

      {/* Platform Info */}
      <div className="rounded-lg border p-4" style={{ borderColor: 'var(--theme-card-border)', backgroundColor: 'var(--theme-card-background)' }}>
        <h4 className="text-sm font-medium mb-2" style={{ color: 'var(--theme-card-text)' }}>
          📱 Sobre Templates de Redes Sociais
        </h4>
        <ul className="space-y-1.5 text-xs" style={{ color: 'var(--theme-muted-text)' }}>
          <li>• Suporta Instagram Direct e Facebook Messenger</li>
          <li>• Mensagem de texto é obrigatória (max 2000 caracteres)</li>
          <li>• Anexo de mídia opcional (imagem, vídeo, áudio ou arquivo)</li>
          <li>• Respostas rápidas aparecem como botões na conversa</li>
          <li>• Use variáveis {'{'}
            {'{'}1{'}}'}
            {'}'}, {'{'}
            {'{'}2{'}}'}
            {'}'} tanto no texto quanto na URL do anexo</li>
          <li>• Payload é usado internamente para identificar a resposta escolhida</li>
        </ul>
      </div>

      {/* Example */}
      <div className="rounded-lg border p-4" style={{ borderColor: 'var(--theme-card-border)', backgroundColor: 'var(--theme-card-background)' }}>
        <h4 className="text-sm font-medium mb-3" style={{ color: 'var(--theme-card-text)' }}>
          💡 Exemplo de Uso
        </h4>
        <div className="space-y-2 text-xs" style={{ color: 'var(--theme-muted-text)' }}>
          <p>
            <strong style={{ color: 'var(--theme-card-text)' }}>Mensagem:</strong> "Olá {'{'}
            {'{'}1{'}}'}
            {'}'}! Seu pedido #{'{'}
            {'{'}2{'}}'}
            {'}'} foi confirmado. O que deseja fazer?"
          </p>
          <p>
            <strong style={{ color: 'var(--theme-card-text)' }}>Anexo:</strong> https://cdn.exemplo.com/produto/
            {'{'}
            {'{'}3{'}}'}
            {'}'}
            .jpg
          </p>
          <p>
            <strong style={{ color: 'var(--theme-card-text)' }}>Respostas Rápidas:</strong>
          </p>
          <ul className="ml-4 space-y-1">
            <li>• "Rastrear Pedido" (payload: track_order)</li>
            <li>• "Falar com Atendente" (payload: talk_to_agent)</li>
            <li>• "Cancelar Pedido" (payload: cancel_order)</li>
          </ul>
        </div>
      </div>
    </div>
  );
};
